import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Touchable } from './touchable';
import {
  getDurabilityStatus,
  subscribeDurability,
  reRequestPersistentStorage,
  type DurabilityStatus,
} from '../storage/durability';

export const DISMISS_KEY = 'knittime-durability-dismissed';
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

function isDismissed(): boolean {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    return !!v && Date.now() - Date.parse(v) < DISMISS_MS;
  } catch {
    return false;
  }
}

function isStandalone(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(display-mode: standalone)').matches;
}

/**
 * Warns when the browser declined persistent storage — the app's data lives
 * in the evictable best-effort bucket (and on iOS Safari, only installed
 * PWAs get persistence at all). Dismissible for 14 days; `unsupported`
 * browsers are never nagged. After a denial, the next user gesture retries
 * the request once — grants correlate with engagement heuristics.
 */
export default function DurabilityBanner() {
  const { t } = useTranslation('app');
  const { pathname } = useLocation();
  const [status, setStatus] = useState<DurabilityStatus>(getDurabilityStatus);
  const [dismissed, setDismissed] = useState(isDismissed);

  useEffect(() => subscribeDurability(() => setStatus(getDurabilityStatus())), []);

  const atRisk = status === 'denied' || status === 'error';

  // Hooks must run before the route/dismissed/standalone early return below,
  // so this effect stays armed (harmlessly) even while the banner is hidden
  // for those reasons — a storage-permission grant from a stray pointerdown
  // only ever helps, never hurts, so there's no need to gate it further.
  useEffect(() => {
    if (!atRisk) return;
    const retry = () => void reRequestPersistentStorage();
    window.addEventListener('pointerdown', retry, { once: true });
    return () => window.removeEventListener('pointerdown', retry);
  }, [atRisk]);

  // Hands-busy surfaces (tracker + just-knit) use large touch targets for
  // their primary Done/Next controls per project convention; this banner is
  // fixed to the bottom and would cover them, so it's suppressed there and
  // shown on calmer routes instead.
  const onHandsBusyRoute = pathname.startsWith('/tracker') || pathname.startsWith('/just-knit');

  if (!atRisk || dismissed || isStandalone() || onHandsBusyRoute) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  return (
    <div
      role="status"
      aria-label={t('durability.aria', 'Storage warning')}
      className="fixed bottom-16 left-0 right-0 z-50 mx-auto flex max-w-xl flex-col gap-2 rounded-t-xl border border-warm bg-warm-soft p-4 shadow-lg sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm text-ink">
        {t(
          'durability.message',
          "This browser may not keep Knit in Time's data permanently. Install the app to your home screen, or back up regularly.",
        )}
      </p>
      <div className="flex shrink-0 items-center gap-2">
        <Link
          to="/learn/install/"
          className="rounded-full bg-primary px-4 py-2 text-sm font-bold text-white"
        >
          {t('durability.install', 'How to install')}
        </Link>
        <Touchable
          haptic="softTap"
          onClick={dismiss}
          aria-label={t('durability.dismiss', 'Dismiss')}
          className="rounded-full px-3 py-2 text-sm font-semibold text-ink-muted"
        >
          ✕
        </Touchable>
      </div>
    </div>
  );
}
